import React, { useEffect, useState } from "react";
import { Button, Breadcrumb } from "antd";

import DataTable from "../components/Tables/Datatable";
import SearchInput from "../components/Forms/SearchInput";
import PrimaryButton from "../components/Forms/PrimaryButton";
import CustomBadge from "../components/Charts/CustomBadge";
import Loader from "../common/Loader";

import { useApiCall } from "../services/api";
import { useSelector } from "react-redux";
import {
  Link,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { formatDate, handleRouteNav } from "../utils";
import Filters from "../components/Filters";
import {
  PurgeButton,
  PurgeModalUI,
  usePurgeModal,
} from "../components/models/PurgeModal";
import usePagination from "../hooks/usePagination";

const statusVariantMap = {
  Approved: "success",
  Pending: "pending",
  Rejected: "danger",
};

const SchemeRequestList = () => {
  const {
    pagination,
    handleChange: handlePaginationChange,
    resetPage,
  } = usePagination();

  const { appliedFilters, ModalUI, FilterButton } = Filters({
    filters: [
      {
        type: "select",
        key: "status",
        label: "Status",
        options: [
          { label: "Approved", value: "Approved" },
          { label: "Rejected", value: "Rejected" },
          { label: "Pending", value: "Pending" },
        ],

        inputProps: {
          placeholder: "Select Status",
        },
      },
      {
        type: "daterange",
        key: "requestDate",
        label: "Date",
      },
    ],
    onChange: () => resetPage(),
  });
  const { postApi, loading } = useApiCall();
  const { user_id, role_id, is_sales_person } = useSelector(
    (state) => state.auth,
  );

  const [offerList, setOfferList] = useState([]);
  const [total, setTotal] = useState(0);

  const purgeHook = usePurgeModal();

  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const navigate = useNavigate();
  const { setTitle, setBackButton, setDescription } = useOutletContext();
  const [clients, setClients] = useState([]);
  const handleChange = (pagination) => {
    handlePaginationChange(pagination);
  };

  const columns = [
    {
      title: "Scheme",
      dataIndex: ["offer", "title"],
      ellipsis: true,
    },
    {
      title: "Request Status",
      dataIndex: "status",
      render: (status) => (
        <CustomBadge variant={statusVariantMap[status]} text={status} />
      ),
    },
    {
      title: "Requested Clients",
      dataIndex: "requested_clients",
      ellipsis: true,

      render: (_, record) => {
        const customers = record?.offer_request_customer_ids
          ? JSON.parse(record.offer_request_customer_ids)
          : [];
        const allRequestedUser = clients.filter((client) =>
          customers.includes(client?.customer_id),
        );

        if (!allRequestedUser.length) return "-";

        const userData = allRequestedUser.map((c) => c.company_name).join(", ");

        return userData;
      },
    },
    is_sales_person
      ? null
      : {
          title: "Requested By",
          dataIndex: "updated_by",
          ellipsis: true,
        },
    {
      title: "Updated on",
      render: (record) => (
        <div>
          {formatDate(record?.updated_at, { format: "DD/MM/YYYY - h:mma" })}
        </div>
      ),
    },
  ].filter(Boolean);

  const fetchClients = async () => {
    try {
      const body = {
        page: 0,
        per_page: 0,
      };
      const res = await postApi("sales/assign-customer-list", body);
      if (!res?.status) {
        setClients([]);
        return;
      }
      if (res && res.data) {
        setClients(res.data);
      }
    } catch (err) {
    } finally {
    }
  };

  useEffect(() => {
    setTitle("Scheme List");
    setBackButton(false);
  }, [setTitle, setBackButton]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    resetPage();
  };

  useEffect(() => {
    setTitle("Scheme Requests");
    setDescription(
      <div>
        <Breadcrumb>
          {[
            { title: "Home", href: handleRouteNav("", role_id) },
            {
              title: "Discounts",
              href: handleRouteNav("/discount-schemes", role_id),
            },
            {
              title: "Scheme Requests",
              href: handleRouteNav(`/scheme-requests`, role_id),
            },
          ].map((item, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={item.href}>{item.title}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>,
    );
    setBackButton(true);
  }, [setTitle, setDescription, setBackButton]);

  const viewScheme = (record) => {
    navigate(handleRouteNav(`/scheme-details`, role_id), {
      state: {
        mode: false,
        offerId: record?.offer_id,
        offerRequestId: record?.offer_request_id,
      },
    });
  };

  const fetchOfferList = async () => {
    const { status, requestDate } = appliedFilters;

    const filters = {
      status,
      from_date: requestDate?.[0]
        ? formatDate(new Date(requestDate[0]), { format: "YYYY-MM-DD" })
        : undefined,
      to_date: requestDate?.[1]
        ? formatDate(new Date(requestDate[1]), { format: "YYYY-MM-DD" })
        : undefined,
    };
    const payload = {
      page: pagination.current,
      per_page: pagination.pageSize,
      search: query,
      user_id: is_sales_person ? user_id : undefined,
      ...(Object.keys(appliedFilters).length ? filters : {}),
    };
    const res = await postApi("sales/offer/offer-request/list", payload);
    if (!res?.status) {
      setOfferList([]);
      setTotal(0);
      return;
    }
    if (res && res.data) {
      setOfferList(res.data);
      setTotal(res.total);
    }
  };

  const handlePurge = async (payload) => {
    const response = await postApi(
      "sales/offer/delete-offer-request-bulk",
      payload,
      true,
    );

    if (response && response.status) {
      fetchOfferList();
    }

    return response.status || false;
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOfferList();
    }, 300);

    return () => clearTimeout(handler);
  }, [pagination.current, pagination.pageSize, query, appliedFilters]);

  useEffect(() => {
    fetchClients();
  }, []);

  return (
    <div className="bg-white rounded-[32px] overflow-hidden">
      <div className="flex justify-between items-center gap-3 px-8 py-6">
        <SearchInput
          placeholder="Search for requests"
          value={query}
          onChange={handleSearchInput}
        />

        <div className="flex items-center gap-3">
          <FilterButton />
          <PrimaryButton
            text="Scheme List"
            onClick={() =>
              navigate(handleRouteNav(`/discount-schemes`, role_id))
            }
            noIcon
          />
          <Button
            type="primary"
            className="h-[45px] bg-[#7070701A]  hover:!bg-[#7070701A] hover:!text-[#A33434] font-bold text-[#A33434] text-base !cursor-default"
          >
            {total} requests
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader size={60} />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={offerList}
            handleChange={handleChange}
            handleRow={viewScheme}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: total,
            }}
            loading={loading}
          />

          {!is_sales_person && (
            <div className="flex justify-end mt-4">
              <PurgeButton
                hookData={purgeHook}
                buttonLabel="Purge Requests"
                className="me-5 mb-3"
              />
            </div>
          )}
        </>
      )}
      <ModalUI />
      <PurgeModalUI
        hookData={purgeHook}
        onConfirm={handlePurge}
        loading={loading}
        title="Purge Requests"
      />
    </div>
  );
};

export default SchemeRequestList;
